"use client";

import type { StableFeature } from "@hisani/stable";
import { Button, Skeleton } from "@hisani/ui";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeftRight,
  Banknote,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  Dumbbell,
  LayoutDashboard,
  Link2,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import * as React from "react";
import { Link } from "../../../../i18n/navigation";
import { loginPath } from "../../../../i18n/paths";
import {
  ApiClientError,
  apiErrorMessage,
  requestApiJson,
} from "../../../api-client";
import { useAuth } from "../../../auth-client";
import { StableDashboardSchema } from "../stable-contracts";
import { STABLE_ERROR_CODES } from "@hisani/stable";
import type { z } from "zod";

export type StableDashboard = z.infer<typeof StableDashboardSchema>;

type StableNavLabelKey =
  | "navDashboard"
  | "navTraining"
  | "navAccounting"
  | "navOwnerLinks"
  | "navHire"
  | "navTransfer";

type StableSection =
  | "dashboard"
  | "horse"
  | "training"
  | "accounting"
  | "owner-links"
  | "hire"
  | "transfer";

type StablePermissions = Partial<
  Record<StableFeature, "FULL" | "VIEW" | "NONE">
>;

interface StableSectionDefinition {
  readonly key: Exclude<StableSection, "horse">;
  readonly icon: LucideIcon;
  readonly labelKey: StableNavLabelKey;
  readonly href: (stableId: string) => string;
  readonly featureGate?: StableFeature;
}

const STABLE_SECTION_DEFS: readonly StableSectionDefinition[] = [
  {
    key: "dashboard",
    icon: LayoutDashboard,
    labelKey: "navDashboard",
    href: (id) => `/stable/stable?id=${id}`,
  },
  {
    key: "training",
    icon: Dumbbell,
    labelKey: "navTraining",
    href: (id) => `/stable/stable/training?stableId=${id}`,
    featureGate: "TRAINING_SCHEDULER_LOGGING",
  },
  {
    key: "accounting",
    icon: Banknote,
    labelKey: "navAccounting",
    href: (id) => `/stable/stable/accounting?stableId=${id}`,
    featureGate: "ACCOUNTING",
  },
  {
    key: "owner-links",
    icon: Link2,
    labelKey: "navOwnerLinks",
    href: (id) => `/stable/stable/owner-links?stableId=${id}`,
    featureGate: "LINK_AN_OWNER",
  },
  {
    key: "hire",
    icon: Briefcase,
    labelKey: "navHire",
    href: (id) => `/stable/stable/hire?stableId=${id}`,
    featureGate: "HIRE",
  },
  {
    key: "transfer",
    icon: ArrowLeftRight,
    labelKey: "navTransfer",
    href: (id) => `/stable/stable/transfer?stableId=${id}`,
    featureGate: "STABLE_TRANSFER",
  },
];

function dashboardKey(stableId: string) {
  return ["stable", "dashboard", stableId] as const;
}

/**
 * Every Stable section resolves the same three things before it can render:
 * an authenticated actor, the Stable's dashboard (name plus effective
 * permissions), and whether this actor may open this section at all. The
 * hook resolves them once so the sections stay presentation, and so the
 * loading, load-failure, not-found, and permission-denied surfaces are
 * identical wherever the Horseman lands.
 */
export type StableWorkspaceState =
  | { readonly status: "loading" }
  | {
      readonly status: "error";
      readonly error: unknown;
      readonly isNotFound: boolean;
    }
  | { readonly status: "denied"; readonly dashboard: StableDashboard }
  | { readonly status: "ready"; readonly dashboard: StableDashboard };

export function useStableWorkspace({
  stableId,
  returnPath,
  feature,
}: {
  readonly stableId: string;
  readonly returnPath: string;
  readonly feature?: StableFeature;
}): {
  readonly state: StableWorkspaceState;
  readonly dashboard: StableDashboard | undefined;
  readonly refetch: () => void;
} {
  const auth = useAuth();
  const locale = useLocale();

  React.useEffect(() => {
    if (auth.status === "anonymous") {
      window.location.replace(loginPath(locale, returnPath));
    }
  }, [auth.status, locale, returnPath]);

  const query = useQuery({
    queryKey: dashboardKey(stableId),
    queryFn: () =>
      requestApiJson(
        `/api/v1/stable/stables/${stableId}`,
        StableDashboardSchema,
      ),
    enabled: auth.status === "authenticated",
  });

  const dashboard = query.data;
  const state: StableWorkspaceState =
    auth.status !== "authenticated" || query.isPending
      ? { status: "loading" }
      : query.isError
        ? {
            status: "error",
            error: query.error,
            isNotFound:
              query.error instanceof ApiClientError &&
              (query.error.code === STABLE_ERROR_CODES.notFound ||
                query.error.code === STABLE_ERROR_CODES.forbidden),
          }
        : dashboard === undefined
          ? { status: "loading" }
          : feature !== undefined &&
              dashboard.effectivePermissions[feature] !== "FULL"
            ? { status: "denied", dashboard }
            : { status: "ready", dashboard };

  return {
    state,
    dashboard,
    refetch: () => void query.refetch(),
  };
}

function StableWorkspaceContainer({
  children,
}: {
  readonly children: React.ReactNode;
}) {
  return (
    <div className="mx-auto w-full min-w-0 max-w-6xl px-[var(--space-page-inline)] sm:px-6 lg:px-8">
      {children}
    </div>
  );
}

function StableBreadcrumb({
  stableId,
  stableName,
  section,
  current,
}: {
  readonly stableId: string;
  readonly stableName: string;
  readonly section: StableSection;
  readonly current: string;
}) {
  const t = useTranslations("Stable");
  const crumbClassName =
    "min-w-0 truncate rounded-xs px-1 -mx-1 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus";

  return (
    <nav
      aria-label={t("breadcrumbLabel")}
      className="hidden min-w-0 flex-wrap items-center gap-1.5 text-sm font-medium text-secondary md:flex"
    >
      <Link href="/stable" className={crumbClassName}>
        {t("myStables")}
      </Link>
      <ChevronRight
        className="size-3.5 shrink-0 text-tertiary rtl:-scale-x-100"
        aria-hidden="true"
      />
      {section === "dashboard" ? (
        <span className="min-w-0 truncate font-semibold text-primary">
          {stableName}
        </span>
      ) : (
        <>
          <Link
            href={`/stable/stable?id=${stableId}`}
            className={crumbClassName}
          >
            {stableName}
          </Link>
          <ChevronRight
            className="size-3.5 shrink-0 text-tertiary rtl:-scale-x-100"
            aria-hidden="true"
          />
          <span className="min-w-0 truncate font-semibold text-primary">
            {current}
          </span>
        </>
      )}
    </nav>
  );
}

function StableSectionNav({
  stableId,
  active,
  permissions,
}: {
  readonly stableId: string;
  readonly active: StableSection;
  readonly permissions: StablePermissions;
}) {
  const t = useTranslations("Stable");
  const visibleSections = STABLE_SECTION_DEFS.filter(
    (section) =>
      section.key === active ||
      section.featureGate === undefined ||
      permissions[section.featureGate] === "FULL",
  );
  const navigationActive = active === "horse" ? "dashboard" : active;

  return (
    <>
      <nav
        aria-label={t("sectionNavLabel")}
        className="mt-3 grid grid-cols-3 border-y border-border-subtle bg-surface md:hidden"
      >
        {visibleSections.map((section) => {
          const Icon = section.icon;
          const isActive = section.key === navigationActive;
          return (
            <Link
              key={section.key}
              href={section.href(stableId)}
              aria-current={isActive ? "page" : undefined}
              className={`relative flex min-h-[56px] min-w-0 flex-col items-center justify-center gap-[4px] px-[4px] py-[6px] text-center text-[12px] leading-[16px] font-semibold transition-[background-color,color,transform] duration-[var(--motion-duration-fast)] ease-[var(--motion-easing-standard)] active:scale-[0.98] focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus motion-reduce:transition-none ${
                isActive
                  ? "bg-brand-subtle text-primary after:absolute after:inset-x-[12px] after:bottom-0 after:h-[2px] after:rounded-full after:bg-brand"
                  : "text-secondary hover:bg-subtle hover:text-primary"
              }`}
            >
              <Icon
                className={`size-[16px] shrink-0 ${isActive ? "text-brand-ink" : "text-tertiary"}`}
                aria-hidden="true"
              />
              <span className="line-clamp-2">{t(section.labelKey)}</span>
            </Link>
          );
        })}
      </nav>

      <nav
        aria-label={t("sectionNavLabel")}
        className="-mb-px mt-5 hidden max-w-full gap-1 overflow-x-auto md:flex"
      >
        {visibleSections.map((section) => {
          const Icon = section.icon;
          const isActive = section.key === navigationActive;
          return (
            <Link
              key={section.key}
              href={section.href(stableId)}
              aria-current={isActive ? "page" : undefined}
              className={`group inline-flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3 pt-2 pb-3 text-sm font-semibold transition-colors focus-visible:rounded-t-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus ${
                isActive
                  ? "border-brand text-primary"
                  : "border-transparent text-secondary hover:border-border-default hover:text-primary"
              }`}
            >
              <Icon
                className={`size-4 shrink-0 ${isActive ? "text-brand-ink" : "text-tertiary group-hover:text-secondary"}`}
                aria-hidden="true"
              />
              {t(section.labelKey)}
            </Link>
          );
        })}
      </nav>
    </>
  );
}

export interface StablePageProps {
  readonly stableId: string;
  readonly stableName: string;
  readonly section: StableSection;
  readonly permissions: StablePermissions;
  readonly icon: LucideIcon;
  readonly title: string;
  readonly description?: string;
  /** Identity chips under the title: role, plan, Stable ID, status. */
  readonly meta?: React.ReactNode;
  readonly actions?: React.ReactNode;
  readonly mobileBackHref?: string;
  readonly mobileBackLabel?: string;
  readonly mobileSubtitle?: React.ReactNode;
  /** Overrides the breadcrumb's trailing crumb where repeating the page's
   * own heading would read oddly, such as a horse's name. */
  readonly breadcrumbCurrent?: string;
  readonly children: React.ReactNode;
}

/**
 * The frame shared by every Stable workspace screen: a raised header band
 * carrying breadcrumb, identity, page actions, and section tabs, over the
 * canvas that holds the section's own content.
 */
export function StablePage({
  stableId,
  stableName,
  section,
  permissions,
  icon: Icon,
  title,
  description,
  meta,
  actions,
  mobileBackHref = "/stable",
  mobileBackLabel,
  mobileSubtitle,
  breadcrumbCurrent,
  children,
}: StablePageProps) {
  const t = useTranslations("Stable");
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="border-b border-border-subtle bg-surface">
        <StableWorkspaceContainer>
          <div className="pt-0 pb-3 md:pb-0 md:pt-5">
            <Link
              href={mobileBackHref}
              className="-ms-2 inline-flex min-h-11 items-center gap-1 rounded-md px-2 text-sm font-semibold text-secondary hover:bg-subtle hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus md:hidden"
            >
              <ChevronLeft
                className="size-5 shrink-0 rtl:-scale-x-100"
                aria-hidden="true"
              />
              {mobileBackLabel ?? t("myStables")}
            </Link>
            <StableBreadcrumb
              stableId={stableId}
              stableName={stableName}
              section={section}
              current={breadcrumbCurrent ?? title}
            />
            <div className="mt-1 flex flex-wrap items-start justify-between gap-3 md:mt-4 md:gap-x-6 md:gap-y-4">
              <div className="flex min-w-[192px] flex-1 items-start gap-4">
                <span className="hidden size-11 shrink-0 items-center justify-center rounded-lg bg-brand-subtle text-brand-ink ring-1 ring-inset ring-brand-strong/20 md:flex">
                  <Icon className="size-5" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <h1 className="text-heading-medium break-words text-primary [overflow-wrap:anywhere] md:text-heading-large">
                    {title}
                  </h1>
                  {section === "dashboard" ? null : (
                    <p
                      dir="auto"
                      className="text-body-small mt-1 truncate text-secondary md:hidden"
                    >
                      {mobileSubtitle ?? stableName}
                    </p>
                  )}
                  {meta === undefined ? null : (
                    <div className="mt-2 flex flex-wrap items-center gap-2 md:gap-x-3">
                      {meta}
                    </div>
                  )}
                  {description === undefined ? null : (
                    <p className="mt-2 hidden max-w-2xl text-sm leading-6 text-secondary md:block">
                      {description}
                    </p>
                  )}
                </div>
              </div>
              {actions === undefined ? null : (
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  {actions}
                </div>
              )}
            </div>
            <StableSectionNav
              stableId={stableId}
              active={section}
              permissions={permissions}
            />
          </div>
        </StableWorkspaceContainer>
      </header>
      <StableWorkspaceContainer>
        <div className="py-4 md:py-8">{children}</div>
      </StableWorkspaceContainer>
    </div>
  );
}

/** The loading shape of a Stable screen: the header band and one content
 * block, so the page does not jump when the data lands. */
export function StablePageSkeleton() {
  const t = useTranslations("Stable");
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-b border-border-subtle bg-surface">
        <StableWorkspaceContainer>
          <div
            role="status"
            aria-label={t("loading")}
            className="grid gap-4 pb-4 pt-0 md:pt-5"
          >
            <Skeleton className="h-4 w-56" />
            <div className="flex items-center gap-4">
              <Skeleton className="size-11 rounded-lg" />
              <div className="grid gap-2">
                <Skeleton className="h-6 w-64" />
                <Skeleton className="h-4 w-40" />
              </div>
            </div>
            <div className="grid grid-cols-3 border-y border-border-subtle md:flex md:border-0">
              {[0, 1, 2, 3, 4, 5].map((item) => (
                <div
                  key={item}
                  className="flex min-h-14 items-center justify-center px-2"
                >
                  <Skeleton className="h-4 w-20" />
                </div>
              ))}
            </div>
          </div>
        </StableWorkspaceContainer>
      </div>
      <StableWorkspaceContainer>
        <div className="grid gap-4 py-4 md:py-8" aria-hidden="true">
          <div className="grid grid-cols-3 divide-x divide-border-subtle border-y border-border-subtle rtl:divide-x-reverse">
            <Skeleton className="h-16 rounded-none" />
            <Skeleton className="h-16 rounded-none" />
            <Skeleton className="h-16 rounded-none" />
          </div>
          <div className="divide-y divide-border-subtle border-y border-border-subtle">
            <Skeleton className="h-20 rounded-none" />
            <Skeleton className="h-20 rounded-none" />
          </div>
        </div>
      </StableWorkspaceContainer>
    </div>
  );
}

/** A blocking message with an optional recovery action, used for a Stable
 * that could not be loaded and for a section this Horseman may not open. */
export function StablePageNotice({
  title,
  description,
  onRetry,
}: {
  readonly title: string;
  readonly description?: string;
  readonly onRetry?: () => void;
}) {
  const t = useTranslations("Stable");
  return (
    <div
      role="alert"
      className="mx-auto flex max-w-md flex-col items-center gap-3 border-y border-border-subtle bg-surface px-4 py-8 text-center"
    >
      <span className="flex size-11 items-center justify-center rounded-full bg-warning-subtle text-warning">
        <TriangleAlert className="size-5" aria-hidden="true" />
      </span>
      <p className="text-base font-semibold leading-6 text-primary">{title}</p>
      {description === undefined ? null : (
        <p className="text-sm leading-5 text-secondary">{description}</p>
      )}
      {onRetry === undefined ? null : (
        <Button type="button" variant="secondary" onClick={onRetry}>
          {t("retry")}
        </Button>
      )}
    </div>
  );
}

/** The standalone failure surface for a Stable that could not be loaded at
 * all — there is no name or permission set yet, so the workspace frame
 * cannot be drawn around it. */
export function StableLoadFailure({
  state,
  onRetry,
}: {
  readonly state: Extract<StableWorkspaceState, { status: "error" }>;
  readonly onRetry: () => void;
}) {
  const t = useTranslations("Stable");
  return (
    <div className="mx-auto w-full max-w-6xl px-4 pt-0 pb-6 sm:px-6 sm:py-6 lg:px-8">
      <Link
        href="/stable"
        className="mb-4 inline-flex min-h-11 items-center gap-1 rounded-md pe-3 text-sm font-semibold text-secondary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
      >
        <ChevronLeft
          className="size-5 shrink-0 rtl:-scale-x-100"
          aria-hidden="true"
        />
        {t("myStables")}
      </Link>
      <StablePageNotice
        title={
          state.isNotFound
            ? t("stableNotFound")
            : apiErrorMessage(state.error, t("loadError"))
        }
        {...(state.isNotFound ? {} : { onRetry })}
      />
    </div>
  );
}

/** A neutral identity chip for the header's metadata row — a role, a plan,
 * a public identifier. Status uses `StatusBadge`; this carries facts that
 * are not a state to act on. */
export function StableMetaChip({
  icon: Icon,
  children,
  tone = "neutral",
  mono = false,
}: {
  readonly icon?: LucideIcon;
  readonly children: React.ReactNode;
  readonly tone?: "neutral" | "brand";
  readonly mono?: boolean;
}) {
  return (
    <span
      className={`inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold leading-4 tracking-[0.2px] ${
        tone === "brand"
          ? "bg-brand-subtle text-brand-ink"
          : "bg-subtle text-secondary"
      }`}
    >
      {Icon === undefined ? null : (
        <Icon className="size-3.5 shrink-0" aria-hidden="true" />
      )}
      <span
        className={`min-w-0 truncate ${mono ? "font-mono uppercase tracking-[0.4px]" : ""}`}
      >
        {children}
      </span>
    </span>
  );
}
